import React from 'react';
import { closeTicketTab, setActiveTicketTab } from '../../../../../actions/ui';

export const useTicketDetailOpenTabs = ({
    dispatch,
    navigate,
    locationState,
    openTicketTabs,
    activeTicketTabId,
    ticketId,
}) => {
    const normalizedTicketTabs = React.useMemo(() => {
        if (!Array.isArray(openTicketTabs)) {
            return [];
        }

        return openTicketTabs
            .map((tab) => ({
                id: String(tab?.id || '').trim(),
                code: String(tab?.code || '').trim(),
                title: String(tab?.title || '').trim(),
            }))
            .filter((tab) => Boolean(tab.id));
    }, [openTicketTabs]);

    const handleSelectTicketTab = React.useCallback((nextTicketId) => {
        const normalizedId = String(nextTicketId || '').trim();
        if (!normalizedId) {
            return;
        }

        const targetTab = normalizedTicketTabs.find((tab) => tab.id === normalizedId);
        dispatch(setActiveTicketTab(normalizedId));

        navigate(`/tasks/detail/${normalizedId}`, {
            state: {
                ...(locationState || {}),
                ticketCode: targetTab?.code || '',
            },
        });
    }, [dispatch, locationState, navigate, normalizedTicketTabs]);

    const handleCloseTicketDetailTab = React.useCallback((event, closingTicketId) => {
        event?.stopPropagation?.();

        const normalizedClosingId = String(closingTicketId || '').trim();
        if (!normalizedClosingId) {
            return;
        }

        const currentIndex = normalizedTicketTabs.findIndex((tab) => tab.id === normalizedClosingId);
        const isClosingActive = String(ticketId || '') === normalizedClosingId;

        let fallbackTab = null;
        if (isClosingActive && currentIndex >= 0) {
            fallbackTab = normalizedTicketTabs[currentIndex + 1] || normalizedTicketTabs[currentIndex - 1] || null;
        }

        dispatch(closeTicketTab(normalizedClosingId));

        if (!isClosingActive) {
            return;
        }

        if (fallbackTab?.id) {
            navigate(`/tasks/detail/${fallbackTab.id}`, {
                state: {
                    ...(locationState || {}),
                    ticketCode: fallbackTab.code || '',
                },
            });
            return;
        }

        navigate('/tasks');
    }, [dispatch, locationState, navigate, normalizedTicketTabs, ticketId]);

    const isTicketTabActive = React.useCallback((tabId) => {
        const normalizedTabId = String(tabId || '');
        return String(ticketId || '') === normalizedTabId || String(activeTicketTabId || '') === normalizedTabId;
    }, [activeTicketTabId, ticketId]);

    return {
        normalizedTicketTabs,
        handleSelectTicketTab,
        handleCloseTicketDetailTab,
        isTicketTabActive,
    };
};
